/**
 * H2HPositionChart — Diverging bar chart for head-to-head comparison.
 *
 * Default (lowerIsBetter=true, e.g. race/qualifying positions):
 *   delta = teammateValue - driverValue
 *   Positive (green) = driver is better (lower position number)
 *
 * Higher-is-better mode (lowerIsBetter=false, e.g. fantasy points):
 *   delta = driverValue - teammateValue
 *   Positive (green) = driver scored more
 *
 * Bars are centered at 0. Tooltip shows the absolute values and the margin.
 */

import { lazy, Suspense, useMemo } from "react";
import { m } from "@/paraglide/messages";
import { getChartTheme, useThemeVersion } from "./chart-theme";

const ReactECharts = lazy(() => import("echarts-for-react"));

interface H2HPositionChartProps {
  labels: string[];
  driverName: string;
  teammateName: string;
  driverPositions: (number | null)[];
  teammatePositions: (number | null)[];
  driverColor: string;
  height?: number;
  /** When false, higher value is better (e.g. fantasy points). Default: true */
  lowerIsBetter?: boolean;
  /** Prefix shown before values in tooltip (e.g. "P" for positions). Default: "P" */
  valuePrefix?: string;
  /** Unit label shown in avg row (e.g. "pos", "pts"). Default: "pos" */
  unitLabel?: string;
}

export function H2HPositionChart({
  labels,
  driverName,
  teammateName,
  driverPositions,
  teammatePositions,
  driverColor,
  height = 250,
  lowerIsBetter = true,
  valuePrefix = "P",
  unitLabel = "pos",
}: H2HPositionChartProps) {
  const { aheadCount, behindCount, avgDelta } = useMemo(() => {
    let ahead = 0;
    let behind = 0;
    let totalDelta = 0;
    let count = 0;
    for (let i = 0; i < labels.length; i++) {
      const dp = driverPositions[i];
      const tp = teammatePositions[i];
      if (dp == null || tp == null) continue;
      const delta = lowerIsBetter ? tp - dp : dp - tp;
      if (delta > 0) ahead++;
      else if (delta < 0) behind++;
      totalDelta += delta;
      count++;
    }
    return {
      aheadCount: ahead,
      behindCount: behind,
      avgDelta: count > 0 ? Math.round((totalDelta / count) * 10) / 10 : 0,
    };
  }, [labels, driverPositions, teammatePositions, lowerIsBetter]);

  const themeVersion = useThemeVersion();
  const option = useMemo(() => {
    const theme = getChartTheme();

    const deltas = labels.map((_, i) => {
      const dp = driverPositions[i];
      const tp = teammatePositions[i];
      if (dp == null || tp == null) return null;
      return lowerIsBetter ? tp - dp : dp - tp;
    });

    return {
      backgroundColor: "transparent",
      tooltip: {
        trigger: "axis" as const,
        backgroundColor: "rgba(0,0,0,0.85)",
        borderColor: "transparent",
        textStyle: { color: "#fff", fontSize: 12 },
        formatter: (params: { dataIndex: number }[]) => {
          const i = params[0]?.dataIndex;
          if (i == null) return "";
          const dp = driverPositions[i];
          const tp = teammatePositions[i];
          if (dp == null || tp == null) return labels[i];
          const delta = lowerIsBetter ? tp - dp : dp - tp;
          const margin = Math.abs(delta);
          const verdict =
            delta > 0
              ? `<span style="color:#22c55e">▲ ${driverName.split(" ").pop()} ${m.f1_h2h_ahead_by()} ${margin}</span>`
              : delta < 0
                ? `<span style="color:#ef4444">▼ ${driverName.split(" ").pop()} ${m.f1_h2h_behind_by()} ${margin}</span>`
                : `<span style="color:#9ca3af">${m.f1_h2h_equal()}</span>`;
          return `<b>${labels[i]}</b><br/>${valuePrefix}${dp} ${m.f1_h2h_vs()} ${valuePrefix}${tp}<br/>${verdict}`;
        },
      },
      grid: { left: 40, right: 16, top: 16, bottom: 36, containLabel: false },
      xAxis: {
        type: "category" as const,
        data: labels,
        axisLabel: {
          color: theme.mutedForeground,
          fontSize: 10,
          rotate: labels.length > 8 ? 45 : 0,
        },
        axisLine: { lineStyle: { color: theme.border } },
        axisTick: { show: false },
      },
      yAxis: {
        type: "value" as const,
        name: unitLabel,
        nameTextStyle: { color: theme.mutedForeground, fontSize: 10 },
        axisLabel: {
          color: theme.mutedForeground,
          fontSize: 10,
          formatter: (v: number) => (v > 0 ? `+${v}` : `${v}`),
        },
        splitLine: { lineStyle: { color: theme.border, opacity: 0.3 } },
        axisLine: {
          onZero: true,
          lineStyle: { color: theme.border, width: 1 },
        },
      },
      series: [
        {
          type: "bar" as const,
          data: deltas,
          barMaxWidth: 20,
          itemStyle: {
            borderRadius: [3, 3, 0, 0],
            color: (params: { value: number | null }) =>
              params.value == null
                ? theme.border
                : params.value > 0
                  ? "#22c55e"
                  : params.value < 0
                    ? "#ef4444"
                    : theme.mutedForeground,
          },
          markLine: {
            silent: true,
            symbol: "none",
            lineStyle: {
              color: theme.mutedForeground,
              type: "dashed",
              width: 1,
            },
            data: [{ yAxis: 0 }],
          },
          animationDuration: 600,
        },
      ],
    };
  }, [
    labels,
    driverPositions,
    teammatePositions,
    driverName,
    driverColor,
    lowerIsBetter,
    valuePrefix,
    unitLabel,
    themeVersion,
  ]);

  const driverShort = driverName.split(" ").pop() ?? driverName;
  const teammateShort = teammateName.split(" ").pop() ?? teammateName;

  return (
    <div>
      {/* Summary row */}
      <div className="flex items-center justify-between px-1 pb-2 text-xs">
        <div className="flex items-center gap-3">
          <span className="text-success font-semibold">
            {m.f1_h2h_ahead_count({
              driver: driverShort,
              count: String(aheadCount),
            })}
          </span>
          <span className="text-destructive font-semibold">
            {m.f1_h2h_behind_count({ count: String(behindCount) })}
          </span>
        </div>
        <span className="text-muted-foreground">
          {m.f1_h2h_avg()}{" "}
          <span
            className={
              avgDelta > 0
                ? "text-success font-mono"
                : avgDelta < 0
                  ? "text-destructive font-mono"
                  : "text-muted-foreground font-mono"
            }
          >
            {avgDelta > 0 ? `+${avgDelta}` : avgDelta}
          </span>{" "}
          {unitLabel} {m.f1_h2h_vs()} {teammateShort}
        </span>
      </div>
      <Suspense fallback={<div style={{ height: `${height}px` }} />}>
        <ReactECharts
          option={option}
          style={{ height: `${height}px`, width: "100%" }}
          opts={{ renderer: "canvas" }}
        />
      </Suspense>
      {/* Legend */}
      <div className="flex items-center justify-center gap-4 pt-1 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-sm bg-success" />
          {m.f1_h2h_legend_ahead({ driver: driverShort })}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-sm bg-destructive" />
          {m.f1_h2h_legend_behind({ driver: driverShort })}
        </span>
      </div>
    </div>
  );
}
